<template>
  <component
    :is="component"
    class="help-center-support-card__component"
    v-bind="attributes"
    v-on="listeners"
  >
    <div class="help-center-support-card__background">
      <i
        class="help-center-support-card__icon"
        :class="post.support_card.icon"
      />
    </div>
    <div class="help-center-support-card__info">
      {{ post.support_card.cta_text }}
      <p class="help-center-support-card__cta">
        {{ post.support_card.cta_title }}
      </p>
    </div>
  </component>

  <HelpCenterError :error="error" />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { prefetchScript } from 'gleam-shared/browser/prefetch'

import useInProgress from 'gleam-shared/composables/useInProgress'
import triggerCapture from 'gleam-shared/domain/triggerCapture'
import HelpCenterError from 'help-center/components/HelpCenterError'

const MARKER = 'HelpCenterSupportCard'

const props = defineProps({
  post: {
    type: Object,
    required: true
  }
})

const error = ref(null)
const [loadInProgress, loadWithProgress] = useInProgress(loadCapture)

const component = computed(() => {
  if (props.post.support_card.url) {
    return 'a'
  } else {
    return 'button'
  }
})

const listeners = computed(() => {
  if (props.post.support_card.url) {
    return {}
  } else {
    return { click: loadWithProgress }
  }
})

const attributes = computed(() => {
  if (props.post.support_card.url) {
    return {
      rel: 'noopener',
      href: props.post.support_card.url,
      target: '_blank'
    }
  } else {
    return {
      disabled: loadInProgress.value
    }
  }
})

onMounted(() => {
  prefetchScript(props.post.support_card.capture_script, MARKER)
})

function loadCapture() {
  return triggerCapture(
    props.post.support_card.capture_key,
    props.post.support_card.capture_script,
    MARKER
  ).catch((err) => {
    error.value = err
  })
}
</script>

<style lang="scss">
@import 'help-center/mixins';

$radius: 6px;

.help-center-support-card {
  &__component {
    @include help-center-zoom;
    @include help-center-shadow;
    @include help-center-highlight-link($selector: '.help-center-support-card__cta');

    gap: 12px;
    display: flex;

    border: 1px solid var(--help-center-border);
    padding: 12px;
    border-radius: $radius;
    background-color:  var(--help-center-card-bg);
  }

  &__background {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;

    width: 42px;
    height: 42px;
    border-radius: $radius;
    background-color: var(--help-center-icon-bg);
  }

  &__icon {
    @include help-center-icon(20);
    color: var(--help-center-highlight);
    flex: none;
  }

  &__info {
    @include help-center-font(14, title);
    color: var(--help-center-accent);
    font-weight: var(--help-center-bold);

    display: flex;
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
